import { Theme } from '@mui/material';
import { MileageSummaryDto } from '@acorn-portal/shared/src/services/telematicsMileageApi';

/** Share of the annual allowance at which the customer is close to their limit. */
const MILEAGE_WARNING_THRESHOLD_PERCENT = 75;

/**
 * A customer is close to their allowance when they have used 75% of it,
 * or when the mileage API raises `exceedsAllowanceWarning`.
 * A false API flag does not hide usage that has already reached 75%.
 */
export const isCloseToMileageLimit = (usedPercentage: number, exceedsAllowanceWarning?: boolean): boolean =>
	exceedsAllowanceWarning === true || usedPercentage >= MILEAGE_WARNING_THRESHOLD_PERCENT;

/**
 * Alert type for mileage warnings.
 * - exceeded: Already over mileage allowance (danger)
 * - predicted-to-exceed: Predicted to exceed by (x) miles (warning)
 * - due-to-exceed: Due to exceed / running low (warning)
 */
export type MileageAlertType = 'exceeded' | 'predicted-to-exceed' | 'due-to-exceed' | null;

/**
 * Alert content containing title, message, and optional severity.
 */
export interface MileageAlertContent {
	/** Title for the alert banner. */
	title: string;
	/** Detailed message for the alert. */
	message: string;
	/** MUI Alert severity: 'error' (danger) or 'warning'. */
	severity: 'error' | 'warning';
}

/**
 * Statistics calculated from mileage data.
 */
export interface MileageStats {
	/** Actual miles driven during the policy term. */
	actualMileage: number;
	/** Remaining miles until the annual limit. */
	milesRemaining: number;
	/** Percentage of annual allowance used (0-100). */
	usedPercentage: number;
	/** Whether the driver has exceeded their mileage allowance. */
	isExceeded: boolean;
	/** Whether the driver is running low on miles (>=75% used). */
	isLowOnMiles: boolean;
}

/**
 * Formats a date to a human-readable display format.
 *
 * @param date - Date to format (Date object, ISO string, or undefined)
 * @returns Formatted date string (e.g., "30 January 2026") or empty string if no date
 *
 * @example
 * ```ts
 * formatPolicyEndDate('2026-01-30'); // Returns "30 January 2026"
 * formatPolicyEndDate(new Date(2026, 0, 30)); // Returns "30 January 2026"
 * formatPolicyEndDate(undefined); // Returns ""
 * ```
 */
export const formatPolicyEndDate = (date: Date | string | undefined): string => {
	if (!date) return '';
	const dateObj = typeof date === 'string' ? new Date(date) : date;
	return dateObj.toLocaleDateString('en-GB', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});
};

/**
 * Calculates mileage statistics from driver and policy data.
 *
 * For non-telematics policies, returns zero usage with full allowance remaining.
 * For telematics policies, calculates actual usage and warning thresholds.
 *
 * @param driverMileage - Miles driven from telematics API (undefined if not available)
 * @param annualMileage - Annual mileage allowance from policy
 * @param isTelematicsEnabled - Whether the policy has telematics enabled
 * @param exceedsAllowanceWarning - Optional API flag indicating warning threshold exceeded
 * @returns Object containing calculated mileage statistics
 *
 * @example
 * ```ts
 * const stats = calculateMileageStats(7500, 10000, true, false);
 * // stats.usedPercentage = 75
 * // stats.isLowOnMiles = true
 * ```
 */
export const calculateMileageStats = (
	driverMileage: number | undefined,
	annualMileage: number,
	_isTelematicsEnabled?: boolean,
	exceedsAllowanceWarning?: boolean,
): MileageStats => {
	if (driverMileage === undefined) {
		return {
			actualMileage: 0,
			milesRemaining: annualMileage,
			usedPercentage: 0,
			isExceeded: false,
			isLowOnMiles: false,
		};
	}

	const actualMileage = driverMileage || 0;
	const milesRemaining = Math.max(0, annualMileage - actualMileage);
	const usedPercentage = annualMileage <= 0 ? 100 : Math.min(100, (actualMileage / annualMileage) * 100);
	const isExceeded = actualMileage > annualMileage;
	const isLowOnMiles = isCloseToMileageLimit(usedPercentage, exceedsAllowanceWarning);

	return {
		actualMileage,
		milesRemaining,
		usedPercentage,
		isExceeded,
		isLowOnMiles,
	};
};

/**
 * Returns the appropriate progress bar color based on mileage usage.
 *
 * Color thresholds:
 * - Exceeded or >=90% used: error (red)
 * - >=75% used: warning (amber)
 * - <75% used: success (green)
 *
 * @param isExceeded - Whether the mileage limit has been exceeded
 * @param usedPercentage - Percentage of annual allowance used (0-100)
 * @param theme - MUI theme object for accessing palette colors
 * @returns Theme color string from the palette
 *
 * @example
 * ```ts
 * const color = getProgressColor(false, 80, theme); // Returns theme.palette.warning.main
 * ```
 */
export const getProgressColor = (isExceeded: boolean, usedPercentage: number, theme: Theme): string => {
	if (isExceeded) return theme.palette.error.main;
	if (usedPercentage >= 90) return theme.palette.error.main;
	if (usedPercentage >= 75) return theme.palette.warning.main;
	return theme.palette.success.main;
};

/**
 * Returns the warning alert title based on mileage state.
 *
 * @param isExceeded - Whether the mileage limit has been exceeded
 * @param isUsingProposedMileage - Whether displaying proposed mileage (e.g., during top-up)
 * @returns Warning title string
 *
 * @example
 * ```ts
 * getWarningTitle(true, false); // Returns "You've exceeded your mileage limit"
 * getWarningTitle(false, true); // Returns "You're running low on miles with this proposed limit"
 * getWarningTitle(false, false); // Returns "You're running low on miles"
 * ```
 */
export const getWarningTitle = (isExceeded: boolean, isUsingProposedMileage: boolean): string => {
	if (isExceeded) return "You've exceeded your mileage limit";
	if (isUsingProposedMileage) return "You're running low on miles with this proposed limit";
	return "You're close to your annual mileage limit";
};

/**
 * Returns the warning alert message based on mileage state.
 *
 * @param isExceeded - Whether the mileage limit has been exceeded
 * @param isUsingProposedMileage - Whether displaying proposed mileage (e.g., during top-up)
 * @returns Warning message string
 *
 * @example
 * ```ts
 * getWarningMessage(true, false);
 * // Returns "You're over your contracted miles. If you don't top up, your policy may be cancelled..."
 * ```
 */
export const getWarningMessage = (isExceeded: boolean, isUsingProposedMileage: boolean): string => {
	if (isExceeded) {
		return 'You have gone over your mileage allowance. If you don’t top up your miles, your policy may be cancelled. To stay covered, top up using the button below';
	}
	if (isUsingProposedMileage) {
		return 'This proposed mileage limit may not be enough for your needs.';
	}
	return "You're getting close to using up your annual mileage allowance. You might want to consider topping up to make sure you don't run out of miles.";
};

/**
 * Determines the type of mileage alert to display based on the summary data.
 *
 * Evaluates the mileage summary and returns the highest priority alert type along with
 * whether the policy is cancellable. Alert priority is determined in the following order
 * (highest to lowest):
 *
 * 1. **Exceeded** - Driver has already exceeded their annual mileage allowance
 * 2. **Predicted to exceed** - System has calculated predicted excess miles based on driving patterns
 * 3. **Due to exceed** - Driver is running low on miles and may exceed their allowance
 *
 * The `isCancellable` flag indicates whether the policy can be cancelled if mileage limits
 * are exceeded. This is determined by checking if the policy is within the first 50 days
 * of the term start date.
 *
 * @param summary - The mileage summary from the telematics API (nullable)
 * @returns An object containing:
 *   - `alertType`: The type of alert to display ('exceeded' | 'predicted-to-exceed' | 'due-to-exceed' | null)
 *   - `isCancellable`: Whether the policy can be cancelled if limits are exceeded
 *
 * @example
 * ```ts
 * // No summary data
 * getMileageAlertType(null);
 * // Returns: { alertType: null, isCancellable: false }
 *
 * // Exceeded mileage
 * getMileageAlertType({ milesDrivenThisTerm: 15000, annualMileageAllowance: 10000, ... });
 * // Returns: { alertType: 'exceeded', isCancellable: true }
 *
 * // Predicted to exceed
 * getMileageAlertType({ predictedExcessMiles: 2000, ... });
 * // Returns: { alertType: 'predicted-to-exceed', isCancellable: true }
 *
 * // Due to exceed warning
 * getMileageAlertType({ exceedsAllowanceWarning: true, ... });
 * // Returns: { alertType: 'due-to-exceed', isCancellable: false }
 * ```
 */
export const getMileageAlertType = (
	summary: MileageSummaryDto | null | undefined,
): { alertType: MileageAlertType; isCancellable: boolean } => {
	if (!summary) return { alertType: null, isCancellable: false };

	const {
		milesDrivenThisTerm = 0,
		annualMileageAllowance = 0,
		exceedsAllowanceWarning,
		predictedExcessMiles,
	} = summary;

	const usedPercentage = Math.min(100, (milesDrivenThisTerm / annualMileageAllowance) * 100);
	// 75% usage is enough on its own. A false API flag must not hide that.
	const isLowOnMiles = isCloseToMileageLimit(usedPercentage, exceedsAllowanceWarning);

	// 1. Exceeded: already over allowance
	if (milesDrivenThisTerm > annualMileageAllowance) {
		return { alertType: 'exceeded', isCancellable: isCancellable(summary?.termStartDate) };
	}

	// 2. Predicted to exceed: system has calculated predicted excess (show miles)
	if (predictedExcessMiles != null && predictedExcessMiles > 0) {
		return { alertType: 'predicted-to-exceed', isCancellable: isCancellable(summary?.termStartDate) };
	}

	// 3. Due to exceed: running low / due to exceed
	if (isLowOnMiles) {
		return { alertType: 'due-to-exceed', isCancellable: isCancellable(summary?.termStartDate) };
	}

	return { alertType: null, isCancellable: false };
};

/**
 * Determines whether a policy can be cancelled based on the term start date.
 *
 * A policy is cancellable if it is within the specified number of days (default 50) from
 * the term start date. This is used in conjunction with mileage alerts to determine if
 * cancellation warnings should be shown to the user.
 *
 * The function calculates the number of days between the term start date and today,
 * and returns true if the policy is within the cancellation threshold period.
 *
 * @param termStartDate - The policy term start date, or undefined if not available
 * @param daysThreshold - The number of days from term start within which the policy is cancellable (default: 50)
 * @returns true if the policy is within the cancellation threshold period, false otherwise
 *
 * @example
 * ```ts
 * // Policy started 30 days ago (within default 50-day threshold)
 * isCancellable(new Date('2026-01-01')); // Returns true
 *
 * // Policy started 60 days ago (exceeds default 50-day threshold)
 * isCancellable(new Date('2025-11-01')); // Returns false
 *
 * // Policy started 30 days ago with custom 20-day threshold
 * isCancellable(new Date('2026-01-01'), 20); // Returns false (30 > 20)
 *
 * // No term start date
 * isCancellable(undefined); // Returns false
 * ```
 */
export function isCancellable(termStartDate: Date | undefined, daysThreshold: number = 50): boolean {
	if (!termStartDate) return false;

	const termStart = new Date(termStartDate);
	const today = new Date();

	const daysSinceTermStart = Math.floor((today.getTime() - termStart.getTime()) / (1000 * 60 * 60 * 24));

	const isWithinDaysThreshold = daysSinceTermStart <= daysThreshold;

	return isWithinDaysThreshold;
}

/**
 * Gets the alert content (title, message, severity) based on the alert type.
 * For 'predicted-to-exceed', pass predictedExcessMiles so the message includes the (x) miles.
 *
 * @param alertType - The type of mileage alert
 * @param predictedExcessMiles - Optional; required for predicted-to-exceed to show "by (x) miles"
 * @returns Object containing title, message and severity for the alert
 */
export const getAlertContent = (
	alertType: MileageAlertType,
	predictedExcessMiles?: number,
	isCancellable?: boolean,
): MileageAlertContent => {
	switch (alertType) {
		case 'exceeded':
			return {
				title: 'You’ve exceeded your mileage limit',
				message:
					'You have gone over your mileage allowance. If you don’t top up your miles, your policy may be cancelled. To stay covered, top up using the button below',
				severity: 'error',
			};
		case 'predicted-to-exceed': {
			const miles = predictedExcessMiles ?? 0;
			return {
				title: 'Miles predicted to exceed',
				message: `Based on your driving you are predicted to exceed your miles by twice as many miles as you originally selected when you took out your policy. You may need to top up by ${miles.toLocaleString()} ${isCancellable ? 'miles to avoid cancellation' : 'miles'}.`,
				severity: 'warning',
			};
		}
		case 'due-to-exceed':
			return {
				title: "You're close to your annual mileage limit",
				message:
					"You're getting close to using up your annual mileage allowance. You might want to consider topping up to make sure you don't run out of miles.",
				severity: 'warning',
			};
		default:
			return { title: '', message: '', severity: 'warning' };
	}
};
